import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Banner, Spinner} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {type FC, useState} from 'react'
import {Finding} from './Finding'
import {FindingsFilterBar} from './FindingsFilterBar'
import {FindingsSummary} from './FindingsSummary'
import {FixFindingDialog} from './FixFindingDialog'
import type {Finding as FindingType} from './types'
import {useFilterFindings} from './use-filter-findings'
import {useFindings} from './use-findings'
import {useIgnoreFindings} from './use-ignore-findings'

interface FindingsProps {
  pullRequestBasePath: string
}

export const Findings: FC<FindingsProps> = props => (
  <ErrorBoundary boundaryName="Findings" fallback={null}>
    <FindingsInner {...props} />
  </ErrorBoundary>
)

const FindingsInner: FC<FindingsProps> = ({pullRequestBasePath}) => {
  const {findings, ignoredEntityIds, freshness, mergeReadiness, overviewSummary, modelsUrl, isLoading, error} =
    useFindings({
      pullRequestBasePath,
    })

  const {openFindings, ignoredFindings, handleDismiss, handleReopen} = useIgnoreFindings(
    findings,
    pullRequestBasePath,
    ignoredEntityIds,
  )

  const {displayedFindings, ...filterProps} = useFilterFindings(openFindings, ignoredFindings)

  const [fixFindings, setFixFindings] = useState<FindingType[] | null>(null)

  const hasNonVisibilityFilters = filterProps.selectedSeverities.size > 0 || filterProps.selectedSources.size > 0

  if (isLoading) {
    return (
      <div className="d-flex flex-justify-center tmp-p-6">
        <Spinner size="large" />
      </div>
    )
  }

  if (error) {
    return (
      <Banner variant="critical" title="Failed to load findings">
        {error}
      </Banner>
    )
  }

  if (freshness === 'pending') {
    return (
      <Blankslate className="border rounded-3">
        <Blankslate.Heading>Processing findings</Blankslate.Heading>
        <Blankslate.Description>
          Findings are being processed. This page will update automatically
        </Blankslate.Description>
      </Blankslate>
    )
  }

  if (openFindings.length === 0 && ignoredFindings.length === 0) {
    return (
      <Blankslate className="border rounded-3">
        <Blankslate.Heading>No findings</Blankslate.Heading>
        <Blankslate.Description>There are no findings for this pull request</Blankslate.Description>
      </Blankslate>
    )
  }

  return (
    <div className="d-flex flex-column width-full">
      <FindingsSummary mergeReadiness={mergeReadiness} overviewSummary={overviewSummary} />
      {freshness === 'stale' && (
        <Banner variant="info" title="Updating findings">
          New findings are being processed. This page will update automatically.
        </Banner>
      )}
      <FindingsFilterBar
        openCount={openFindings.length}
        resolvedCount={ignoredFindings.length}
        displayedCount={displayedFindings.length}
        handleFix={() => setFixFindings(displayedFindings)}
        handleReopen={() => handleReopen(displayedFindings)}
        handleDismiss={() => handleDismiss(displayedFindings)}
        {...filterProps}
      />
      {displayedFindings.length === 0 && (
        <Blankslate className="border rounded-3">
          <Blankslate.Heading>
            {hasNonVisibilityFilters
              ? 'No findings match your filters'
              : filterProps.selectedVisibility === 'open'
                ? 'All findings resolved'
                : 'No resolved findings'}
          </Blankslate.Heading>
          <Blankslate.Description>
            {hasNonVisibilityFilters
              ? 'Try changing or clearing the filters to see more findings'
              : filterProps.selectedVisibility === 'open'
                ? 'All findings for this pull request have been resolved'
                : 'There are no resolved findings for this pull request'}
          </Blankslate.Description>
        </Blankslate>
      )}
      <ul>
        {displayedFindings.map(finding => (
          <Finding
            key={finding.id}
            finding={finding}
            resolved={filterProps.selectedVisibility === 'resolved'}
            onDismiss={() => handleDismiss([finding])}
            onReopen={() => handleReopen([finding])}
            onFix={() => setFixFindings([finding])}
            feedbackPath={`${pullRequestBasePath}/code_review_findings/feedback`}
          />
        ))}
      </ul>
      {fixFindings && (
        <FixFindingDialog
          findings={fixFindings}
          basePath={pullRequestBasePath}
          modelsUrl={modelsUrl}
          onClose={() => setFixFindings(null)}
        />
      )}
    </div>
  )
}
